import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Home, Building2, Factory, CheckCircle2, ArrowRight } from 'lucide-react';

const Instalaciones = () => {
  const residentialFeatures = [
    'Viviendas unifamiliares',
    'Adosados y chalets',
    'Comunidades de vecinos',
    'Áticos y terrazas',
    'Casas rurales',
    'Segundas residencias'
  ];

  const businessFeatures = [
    'Naves industriales',
    'Oficinas corporativas',
    'Centros comerciales',
    'Hoteles y hostelería',
    'Almacenes y logística',
    'Instalaciones agrícolas'
  ];

  const installationImages = [
    {
      url: 'https://images.pexels.com/photos/30440447/pexels-photo-30440447.jpeg',
      title: 'Instalación Residencial',
      type: 'Vivienda Unifamiliar'
    },
    {
      url: 'https://images.unsplash.com/photo-1726866492047-7f9516558c6e',
      title: 'Instalación Comercial',
      type: 'Parking Solar'
    },
    {
      url: 'https://images.pexels.com/photos/8853504/pexels-photo-8853504.jpeg',
      title: 'Proceso de Instalación',
      type: 'Técnicos Certificados'
    },
    {
      url: 'https://images.unsplash.com/photo-1613665813446-82a78c468a1d',
      title: 'Sistema Completo',
      type: 'Proyecto Finalizado'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">
              Nuestras Instalaciones
            </h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Cada proyecto es único. Adaptamos nuestras soluciones a las características específicas de tu propiedad
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-nubia-text-dark/70 leading-relaxed mb-8">
              En Nubia Solar realizamos un estudio detallado de cada ubicación para diseñar el sistema 
              fotovoltaico más eficiente. Analizamos la orientación, inclinación, sombreado y espacio 
              disponible para garantizar el máximo rendimiento de tu instalación solar.
            </p>
            <p className="text-lg text-nubia-text-dark/70 leading-relaxed">
              Trabajamos con los mejores materiales del mercado y nuestros técnicos certificados 
              garantizan instalaciones de máxima calidad que durarán más de 25 años.
            </p>
          </div>
        </div>
      </section>

      {/* Residential Installations */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 bg-nubia-gold-soft/20 rounded-2xl flex items-center justify-center mb-6">
                  <Home className="w-8 h-8 text-nubia-gold-primary" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
                  Instalaciones Residenciales
                </h2>
                <p className="text-lg text-nubia-text-dark/70 mb-8 leading-relaxed">
                  Diseñamos e instalamos sistemas fotovoltaicos para todo tipo de viviendas. 
                  Desde pequeños sistemas para áticos hasta grandes instalaciones para comunidades 
                  de vecinos, siempre con el objetivo de maximizar tu ahorro energético.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {residentialFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-nubia-green-support mt-0.5 flex-shrink-0" />
                      <span className="text-nubia-text-dark/70">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <img 
                  src="https://images.pexels.com/photos/30440447/pexels-photo-30440447.jpeg"
                  alt="Instalación Residencial"
                  className="rounded-2xl nubia-shadow-lg w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Installations */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="md:order-2">
                <div className="w-16 h-16 bg-nubia-green-support/20 rounded-2xl flex items-center justify-center mb-6">
                  <Building2 className="w-8 h-8 text-nubia-green-support" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
                  Instalaciones para Empresas
                </h2>
                <p className="text-lg text-nubia-text-dark/70 mb-8 leading-relaxed">
                  Soluciones a gran escala para todo tipo de negocios e industrias. Reducimos 
                  tus costes operativos con sistemas fotovoltaicos dimensionados para cubrir 
                  las necesidades energéticas de tu empresa.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {businessFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-nubia-gold-primary mt-0.5 flex-shrink-0" />
                      <span className="text-nubia-text-dark/70">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1726866492047-7f9516558c6e"
                  alt="Instalación Empresarial"
                  className="rounded-2xl nubia-shadow-lg w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Adaptation Process */}
      <section className="py-24 bg-nubia-green-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Adaptación a Cada Proyecto
            </h2>
            <p className="text-xl text-nubia-white/90 mb-12 leading-relaxed">
              Cada instalación es única y requiere un diseño personalizado. Nuestro equipo 
              de ingenieros estudia en detalle las características de tu ubicación para garantizar 
              el máximo rendimiento y ahorro.
            </p>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <Card className="bg-white/10 backdrop-blur border-nubia-gold-primary/30">
                <CardHeader>
                  <CardTitle className="text-white">Análisis Previo</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-white/80">
                    Estudio de orientación, inclinación, sombreado y estructura del tejado.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur border-nubia-gold-primary/30">
                <CardHeader>
                  <CardTitle className="text-white">Diseño Personalizado</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-white/80">
                    Configuración del sistema adaptada a tu consumo y características.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur border-nubia-gold-primary/30">
                <CardHeader>
                  <CardTitle className="text-white">Instalación Profesional</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-white/80">
                    Ejecución impecable por técnicos certificados con materiales premium.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Nuestros Proyectos
            </h2>
            <p className="text-xl text-nubia-text-dark/70">
              Ejemplos de instalaciones realizadas con éxito
            </p>
          </div>
          
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8">
            {installationImages.map((image, index) => (
              <div key={index} className="relative group overflow-hidden rounded-2xl nubia-shadow-lg">
                <img 
                  src={image.url}
                  alt={image.title}
                  className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-nubia-green-primary/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{image.title}</h3>
                  <p className="text-nubia-gold-soft">{image.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
            ¿Quieres Ver Tu Proyecto?
          </h2>
          <p className="text-xl text-nubia-text-dark/70 mb-10 max-w-3xl mx-auto">
            Solicita un estudio personalizado sin compromiso y descubre el potencial solar de tu propiedad
          </p>
          <Link to="/contacto">
            <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
              Solicitar Estudio Gratis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Instalaciones;